import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { MANUAL_ACCESS_AUDIENCES, type ApiError, type ManagedOperator, type ManualAccessAudience, type ManualAccessGrant } from '@vello/api-client';
import {
  Badge, Button, Checkbox, Cluster, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, ListTable, Notice, PageHeader, RadioGroup, SearchInput, Select, Stack, Text,
  type Column,
} from '@vello/web-theme';
import {
  OPERATOR_ACTION_REASON_CODES, formatMarketInstant, isValidTimeZone, scopeTimeZone,
  type OperatorActionReasonCode,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

type Msg = { tone: 'success' | 'danger' | 'warning'; text: string };

/**
 * Configurações › **Acesso ao manual** (D597 MAN-ACCESS, ADR-0120). The platform manual has two open sections (riders, partners)
 * and two closed ones (Equipe Vello, Desenvolvimento); this list decides who reads the closed ones. A reader is an operator
 * account, which may still be INVITED: an outsider is invited in Operadores with no role and granted a section here, before or
 * after finishing the sign-up. Granting both sections is two calls; revoking asks for a reason and, when it was the person's
 * last section, ends their manual sessions. The whole screen sits behind `manual_access:manage` (platform, SUPER_ADMIN).
 */
export function ManualAccessScreen(): ReactElement {
  const { runtime, me, scope, markets } = useAdmin();
  const { confirm } = useConsole();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const api = runtime.manualAccess;

  const [grants, setGrants] = useState<readonly ManualAccessGrant[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  // The drawer: the operator directory, the search, the pick, the sections and the reason.
  const [drawer, setDrawer] = useState(false);
  const [operators, setOperators] = useState<readonly ManagedOperator[] | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [sections, setSections] = useState<readonly ManualAccessAudience[]>([]);
  const [reason, setReason] = useState<OperatorActionReasonCode>('operator_onboarding');
  const [formNote, setFormNote] = useState<string | undefined>(undefined);

  const refusal = useCallback((e: ApiError): string => (e.code === 'FORBIDDEN' ? t('mac.denied') : t('mac.failed', { code: fail(e) })), [t, fail]);

  const tz = scopeTimeZone(scope.current, markets) ?? (isValidTimeZone(me?.timezone) ? me?.timezone : undefined);
  const when = (iso: string): string => formatMarketInstant(iso, tz, lang, 'datetime');

  const load = useCallback(async (): Promise<void> => {
    const res = await api.list();
    if (res.ok) {
      setGrants(res.value.grants);
      setLoadError(undefined);
    } else {
      setGrants([]);
      setLoadError(refusal(res.error));
    }
  }, [api, refusal]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDrawer = async (): Promise<void> => {
    setMsg(undefined);
    setQuery('');
    setPicked(undefined);
    setSections([]);
    setReason('operator_onboarding');
    setFormNote(undefined);
    setDrawer(true);
    if (operators === undefined) {
      const res = await runtime.operatorsAdmin.list();
      if (res.ok) setOperators(res.value.operators);
      else {
        setOperators([]);
        setFormNote(refusal(res.error));
      }
    }
  };

  const sectionName = (a: string): string => v('manual_access.audience', a);
  const nameOf = (g: { displayName: string; email: string }): string => (g.displayName.trim() !== '' ? g.displayName : g.email);

  const onRevoke = async (g: ManualAccessGrant): Promise<void> => {
    const labels = OPERATOR_ACTION_REASON_CODES.map((c) => v('operator.action_reason', c));
    const answer = await confirm({
      title: t('mac.revoke.title', { who: nameOf(g) }), body: t('mac.revoke.body', { section: sectionName(g.audience) }),
      confirmLabel: t('mac.revoke'), cancelLabel: t('mac.cancel'), tone: 'danger', icon: 'shieldAlert',
      reasons: labels, reasonLabel: t('mac.revoke.reason'), reasonsOnly: true,
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const code = OPERATOR_ACTION_REASON_CODES[labels.indexOf(answer.reason)];
    if (code === undefined) return;
    const res = await api.revoke(g.id, code);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    const base = res.value.changed ? t('mac.revoked', { who: nameOf(g), section: sectionName(g.audience) }) : t('mac.revoked.already');
    setMsg({ tone: 'success', text: res.value.sessionsRevoked > 0 ? `${base} ${t('mac.revoked.sessions', { n: res.value.sessionsRevoked })}` : base });
    await load();
  };

  const eligible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (operators ?? [])
      .filter((o) => o.status !== 'DEACTIVATED')
      .filter((o) => q === '' || (o.displayName ?? '').toLowerCase().includes(q) || o.identifier.toLowerCase().includes(q));
  }, [operators, query]);

  const held = useMemo(() => new Set((grants ?? []).filter((g) => g.userId === picked).map((g) => g.audience)), [grants, picked]);
  const pickedOperator = (operators ?? []).find((o) => o.userId === picked);

  const toggleSection = (a: ManualAccessAudience, on: boolean): void => setSections((cur) => (on ? [...cur.filter((x) => x !== a), a] : cur.filter((x) => x !== a)));

  const onGrant = async (): Promise<void> => {
    if (picked === undefined || pickedOperator === undefined) {
      setFormNote(t('mac.drawer.pickPerson'));
      return;
    }
    const chosen = MANUAL_ACCESS_AUDIENCES.filter((a) => sections.includes(a) && !held.has(a));
    if (chosen.length === 0) {
      setFormNote(t('mac.drawer.pickSection'));
      return;
    }
    setBusy(true);
    setFormNote(undefined);
    const created: ManualAccessAudience[] = [];
    const existing: ManualAccessAudience[] = [];
    let failure: ApiError | undefined;
    for (const audience of chosen) {
      const res = await api.grant({ userId: picked, audience, reasonCode: reason });
      if (!res.ok) {
        failure = res.error;
        break;
      }
      (res.value.created ? created : existing).push(audience);
    }
    setBusy(false);
    const who = pickedOperator.displayName ?? pickedOperator.identifier;
    const names = (list: readonly ManualAccessAudience[]): string => list.map(sectionName).join(', ');
    if (failure !== undefined) {
      // A first section may already be saved when the second is refused: say so, then show the refusal in the drawer.
      if (created.length > 0 || existing.length > 0) setMsg({ tone: 'warning', text: t('mac.granted', { who, sections: names([...created, ...existing]) }) });
      setFormNote(refusal(failure));
      await load();
      return;
    }
    const parts: string[] = [];
    if (created.length > 0) parts.push(t('mac.granted', { who, sections: names(created) }));
    if (existing.length > 0) parts.push(t('mac.granted.already', { who, sections: names(existing) }));
    setMsg({ tone: 'success', text: parts.join(' ') });
    setDrawer(false);
    await load();
  };

  const columns: readonly Column<ManualAccessGrant>[] = [
    {
      key: 'person', header: t('mac.col.person'), width: 'minmax(0, 2fr)',
      render: (g) => (
        <div>
          <strong>{nameOf(g)}</strong>
          <Text role="caption" tone="secondary" block>{g.email}</Text>
        </div>
      ),
    },
    {
      key: 'section', header: t('mac.col.section'), width: 'minmax(0, 1fr)',
      render: (g) => (
        <Cluster gap={2} wrap>
          <Badge tone="neutral">{sectionName(g.audience)}</Badge>
          {!g.accountActive && <Badge tone="warning">{t('mac.badge.inactive')}</Badge>}
        </Cluster>
      ),
    },
    {
      key: 'by', header: t('mac.col.grantedBy'), width: 'minmax(0, 1.4fr)',
      render: (g) => (
        <div>
          <span>{g.grantedByName}</span>
          <Text role="caption" tone="secondary" block>{v('operator.action_reason', g.grantReasonCode)}</Text>
        </div>
      ),
    },
    { key: 'at', header: t('mac.col.grantedAt'), width: 'minmax(0, 1fr)', render: (g) => <span className="tnum">{when(g.grantedAt)}</span> },
  ];

  const rows = grants ?? [];

  return (
    <div>
      <PageHeader
        title={t('mac.title')}
        subtitle={t('mac.subtitle')}
        actions={<Button variant="primary" onClick={() => void openDrawer()} data-testid="mac-grant-open">{t('mac.grant')}</Button>}
      />
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <div data-testid="mac-list">
        <ListTable
          columns={columns}
          rows={rows}
          rowKey={(g) => g.id}
          actions={(g) => <Button variant="danger" size="sm" onClick={() => void onRevoke(g)} data-testid={`mac-revoke-${g.id}`}>{t('mac.revoke')}</Button>}
          actionsWidth="max-content"
          {...(grants !== undefined && grants.length > 0 ? { countLabel: t('mac.count', { n: grants.length }) } : {})}
          loading={grants === undefined}
          loadingLabel={t('mac.loading')}
          {...(loadError !== undefined ? { error: loadError, onRetry: () => void load(), retryLabel: t('mac.retry') } : {})}
          empty={<EmptyState title={t('mac.empty.title')} detail={t('mac.empty.detail')} />}
          minWidth={720}
        />
      </div>

      <Drawer open={drawer} onClose={() => setDrawer(false)} labelledBy="mac-drawer-title">
        <DrawerHeader titleId="mac-drawer-title" title={t('mac.drawer.title')} subtitle={t('mac.drawer.subtitle')} onClose={() => setDrawer(false)} closeLabel={t('mac.close')} />
        <DrawerBody>
          <Stack gap={4}>
            {formNote !== undefined && <Notice tone="danger">{formNote}</Notice>}
            <Notice tone="neutral">
              {t('mac.drawer.outsider')}{' '}
              <Button variant="ghost" size="sm" onClick={() => navigate('operators')} data-testid="mac-open-operators">{t('mac.drawer.outsider.link')}</Button>
            </Notice>
            <Field label={t('mac.drawer.person')} htmlFor="mac-search">
              <Stack gap={3}>
                <SearchInput id="mac-search" value={query} onChange={setQuery} onClear={() => setQuery('')} placeholder={t('mac.drawer.search')} clearLabel={t('mac.drawer.searchClear')} />
                {operators !== undefined && eligible.length === 0 && <Text role="caption" tone="secondary" block>{t('mac.drawer.noOperators')}</Text>}
                {eligible.length > 0 && (
                  <RadioGroup
                    name="mac-operator"
                    ariaLabel={t('mac.drawer.person')}
                    value={picked}
                    onChange={(id) => { setPicked(id); setFormNote(undefined); }}
                    options={eligible.map((o) => ({
                      value: o.userId,
                      label: (
                        <span data-testid={`mac-operator-${o.userId}`}>
                          <strong>{o.displayName ?? o.identifier}</strong>{' '}
                          <Badge tone={o.status === 'ACTIVE' ? 'success' : 'warning'}>{v('operator.status', o.status)}</Badge>
                          <Text role="caption" tone="secondary" block>{o.identifier}</Text>
                        </span>
                      ),
                    }))}
                  />
                )}
              </Stack>
            </Field>
            <div role="group" aria-label={t('mac.drawer.sections')} data-testid="mac-sections">
              <strong>{t('mac.drawer.sections')}</strong>
              {MANUAL_ACCESS_AUDIENCES.map((a) => (
                <Checkbox
                  key={a}
                  checked={held.has(a) || sections.includes(a)}
                  disabled={held.has(a)}
                  onChange={(on) => toggleSection(a, on)}
                  label={held.has(a) ? `${sectionName(a)} · ${t('mac.drawer.held')}` : sectionName(a)}
                  data-testid={`mac-section-${a}`}
                />
              ))}
            </div>
            <Field label={t('mac.drawer.reason')} htmlFor="mac-reason">
              <Select id="mac-reason" value={reason} onChange={(e) => setReason(e.target.value as OperatorActionReasonCode)}>
                {OPERATOR_ACTION_REASON_CODES.map((c) => <option key={c} value={c}>{v('operator.action_reason', c)}</option>)}
              </Select>
            </Field>
          </Stack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={() => setDrawer(false)}>{t('mac.cancel')}</Button>
          <span className="vello-grow" />
          <Button variant="secondary" disabled={busy} onClick={() => void onGrant()} data-testid="mac-grant-send">{t('mac.drawer.submit')}</Button>
        </DrawerFooter>
      </Drawer>
    </div>
  );
}
